<template>
  <div class="center-stats-panel neumorphism-out">
    <div v-if="center">
      <div class="title-image">
        <div class="text-h5">
          {{ `Dawan ${center.name} ` }}
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-icon @click="returnCenterList" v-on="on"
                >mdi-arrow-left-circle-outline</v-icon
              >
            </template>
            <span>{{ $t("crudCenter.redirect") }}</span>
          </v-tooltip>
        </div>
      </div>
      <v-simple-table theme="grey">
        <tbody>
          <tr>
            <th>{{ $t("BookingOverview.countRooms") }}</th>
            <td>{{ roomsCount }}</td>
          </tr>
          <tr>
            <th>{{ $t("BookingOverview.fullCapacity") }}</th>
            <td>{{ fullCapacitySum }}</td>
          </tr>
          <tr>
            <th>{{ $t("BookingOverview.reducedCapacity") }}</th>
            <td>{{ reducedCapacitySum }}</td>
          </tr>
        </tbody>
      </v-simple-table>
      <div>
        <div class="datepicker" v-if="filterAvailable !== 'available'">
          <p>{{ formatDate(selectedDate, this.$i18n.locale) }}</p>
          <v-btn
            @click="openPickerTime"
            icon
            rounded
            class="neumorphism-out-animated"
            :aria-label="$t('timeDirection.btnSelectDate')"
          >
            <v-icon color="red darken-1">mdi-calendar-search</v-icon>

            <template v-if="showPickerTime">
              <v-row class="pickerTimeDirection" justify="center">
                <v-date-picker
                  @change="updateSelectedDate()"
                  v-model="selectedDate"
                  :locale="this.$i18n.locale"
                  color="red darken-1"
                  show-adjacent-months
                  :landscape="true"
                  v-click-outside="closePickerTimeDirection"
                >
                </v-date-picker>
              </v-row>
            </template>
          </v-btn>
        </div>
        <div class="radiogroup">
          <v-radio-group v-model="filterAvailable" row mandatory>
            <v-radio
              :label="$t('CenterStats.radioAvailable')"
              value="available"
              color="red darken-1"
            ></v-radio>
            <v-radio
              :label="$t('CenterStats.radioBooking')"
              value="booking"
              color="red darken-1"
            ></v-radio>
          </v-radio-group>
          <v-radio-group v-model="filterResources" row mandatory>
            <v-radio
              :label="$t('CenterStats.radioRoom')"
              value="room"
              color="red darken-1"
            ></v-radio>
            <v-radio
              :label="$t('CenterStats.radioDesk')"
              value="desk"
              color="red darken-1"
            ></v-radio>
            <v-radio
              :label="$t('CenterStats.radioEquipment')"
              value="equipment"
              color="red darken-1"
            ></v-radio>
          </v-radio-group>
        </div>
      </div>
    </div>

    <OccupationChart
      :unavailableResources="unavailableResources"
      :resourcesCount="resourcesCount"
      :filterAvailable="filterAvailable"
      :filterResources="filterResources"
      v-if="resourcesCount !== null && resourcesCount !== 0"
    ></OccupationChart>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapGetters } from "vuex";
import OccupationChart from "./subcomponent/OccupationChart.vue";
export default {
  name: "OverviewStats",
  components: {
    OccupationChart,
  },
  data() {
    return {
      selectedDate: new Date().toISOString().substr(0, 10),
      showPickerTime: false,
      bookedResources: new Set(),
      filterAvailable: null,
      filterResources: null,
    };
  },
  props: {
    center: null,
    roomsCount: null,
    fullCapacitySum: null,
    reducedCapacitySum: null,
  },
  filters: {},
  watch: {
    selectedDate: {
      handler: function (newDate) {
        this.updateSelectedDate();
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters("bookingModule", {
      bookingsByCenter: "getBookingsByCenterId",
      bookedResourcesForDay: "bookedResourcesForDay",
      bookedResourcesByTypeForDay: "bookedResourcesByTypeForDay",
    }),
    ...mapGetters("roomModule", {
      roomsByCenter: "getRoomsByCenterId",
      roomsUnavailableByCenter: "getUnavailableRoomsByCenter",
    }),
    ...mapGetters("deskModule", {
      desksByCenterId: "getDesksByCenterId",
      desksUnavailableByCenter: "getUnavailableDesksByCenter",
    }),
    ...mapGetters("equipmentModule", {
      equipmentsByCenter: "getEquipmentsByCenterId",
      equipmentsUnavailableByCenter: "getUnavailableEquipmentsByCenter",
    }),
    bookings() {
      return this.bookingsByCenter(parseInt(this.$route.params.id));
    },
    resourcesCount() {
      switch (this.filterResources) {
        case "room":
          return this.roomsByCenter(this.center.id).length;
        case "desk":
          return this.desksByCenterId(this.center.id).length;
        case "equipment":
          return this.equipmentsByCenter(this.center.id).length;
        default:
          return null;
      }
    },
    unavailableResources() {
      if (this.filterAvailable === "available") {
        switch (this.filterResources) {
          case "room":
            return this.roomsUnavailableByCenter(this.center).length;
          case "desk":
            return this.desksUnavailableByCenter(this.center.id).length;
          case "equipment":
            return this.equipmentsUnavailableByCenter(this.center).length;
          default:
            return null;
        }
      } else {
        return this.bookedResourcesByTypeForDay(
          DateTime.fromISO(this.selectedDate),
          this.center.id,
          this.filterResources
        ).length;
      }
    },
  },
  methods: {
    returnCenterList(item) {
      this.$router.push({
        name: "ViewCenters",
      });
    },
    closePickerTimeDirection() {
      this.showPickerTime = false;
    },
    openPickerTime() {
      this.showPickerTime = !this.showPickerTime;
    },
    updateSelectedDate() {
      this.bookedResources = new Set();
      this.bookedResources = this.bookedResourcesForDay(
        DateTime.fromISO(this.selectedDate),
        parseInt(this.$route.params.id)
      );
    },
    formatDate(dateString, locale) {
      return DateTime.fromISO(dateString).toLocaleString({
        locale,
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
  },
  async created() {},
};
</script>

<style lang="scss" scoped>
.center-stats-panel {
  padding: 5px 20px;
  border-radius: 20px;
  display: flex;

  .title-image {
    margin: 5px 12px 5px 0px;
  }
}

.datepicker {
  display: flex;
  margin-top: 10px;
  justify-content: space-between;
}
.pickerTimeDirection {
  position: absolute;
  z-index: 3;
  margin-top: 22rem;
}
</style>
